import { useEffect } from 'react'

import { Spacing } from '../../../enums/spacing'
import { rwd } from '../../../utils/rwd'
import { DRAWER_COMPACT_WIDTH, DRAWER_OFFSET_CSS_VAR } from '../constants/drawerOffsetCssVars'

interface UsePushContentOffsetProps {
  isCompact: boolean
}

const ZERO_OFFSET = '0px'
const PUSH_OFFSET = `calc(${DRAWER_COMPACT_WIDTH} + ${Spacing.S8})`

/**
 * While the Drawer is in Compact mode at or above xxxlarge, publish the offset on `:root` so
 * consumer layouts wrapped in `<Drawer.SqueezeZone>` reserve space. Resets to 0 otherwise and on
 * cleanup.
 */
export const usePushContentOffset = ({ isCompact }: UsePushContentOffsetProps): void => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const mediaQuery = window.matchMedia(`(min-width: ${rwd.getBreakpointValue('xxxlarge')}px)`)

    const applyOffset = () => {
      const shouldPush = isCompact && mediaQuery && mediaQuery.matches
      const nextOffset = shouldPush ? PUSH_OFFSET : ZERO_OFFSET
      document.documentElement.style.setProperty(DRAWER_OFFSET_CSS_VAR, nextOffset)
    }

    applyOffset()
    mediaQuery?.addEventListener('change', applyOffset)

    return () => {
      mediaQuery?.removeEventListener('change', applyOffset)
      document.documentElement.style.setProperty(DRAWER_OFFSET_CSS_VAR, ZERO_OFFSET)
    }
  }, [isCompact])
}
